import {
  DEFAULT_SERVER_CONSOLE_LAYOUT,
  DEFAULT_SERVER_OVERVIEW_LAYOUT,
  type ClientServer,
  type Layout,
} from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Info, ServerCog, SquareTerminal } from 'lucide-react';
import type { ReactNode } from 'react';
import { Builder } from '@/components/customisation/builder/builder';
import {
  ServerPageProvider,
  type ServerPage,
} from '@/components/customisation/render/server-context';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSiteDraft } from '@/lib/customisation/site-draft';
import { myServersQuery } from '@/lib/server-queries';
import { cn } from '@/lib/utils';

type Which = 'overview' | 'console';

export const Route = createFileRoute('/_app/admin/customisation/server')({
  validateSearch: (s: Record<string, unknown>): { page?: Which; preview?: string } => ({
    ...(s.page === 'console' ? { page: 'console' as const } : {}),
    ...(typeof s.preview === 'string' && /^[a-z0-9-]{1,36}$/i.test(s.preview)
      ? { preview: s.preview }
      : {}),
  }),
  component: Page,
});

const PAGES: Record<
  Which,
  {
    label: string;
    icon: typeof ServerCog;
    key: 'serverOverview' | 'serverConsole';
    surface: ServerPage['surface'];
    defaults: Layout;
    note: string;
  }
> = {
  overview: {
    label: 'Overview',
    icon: ServerCog,
    key: 'serverOverview',
    surface: 'server-overview',
    defaults: DEFAULT_SERVER_OVERVIEW_LAYOUT,
    note: 'Every server’s overview tab. The header with the server’s name and power buttons, and the section tabs, stay above this layout.',
  },
  console: {
    label: 'Console',
    icon: SquareTerminal,
    key: 'serverConsole',
    surface: 'server-console',
    defaults: DEFAULT_SERVER_CONSOLE_LAYOUT,
    note: 'Every server’s console tab. The console and the status alerts are required: move them anywhere, but they can’t be removed or hidden.',
  },
};

/**
 * Builders for the two server pages every customer sees. Both layouts live in the site draft
 * (`serverOverview`, `serverConsole`), so undo, autosave and publishing are the studio's.
 * Widgets preview with one of your servers when you pick one; the console is always a
 * placeholder here, so nothing typed in the studio can reach a server.
 */
function Page() {
  const draft = useSiteDraft();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const which: Which = search.page ?? 'overview';
  const page = PAGES[which];
  const servers = useQuery(myServersQuery('all'));
  const list = servers.data?.data ?? [];
  const previewServer =
    list.find((s) => s.identifier === search.preview) ??
    (search.preview === 'none' ? undefined : list[0]);

  const builder = (
    <Builder
      key={which}
      tenant={draft.tenant}
      surface={page.surface}
      resetLabel={`Hearth’s default server ${page.label.toLowerCase()} page`}
      layout={draft.site[page.key]}
      onChange={(next, label) =>
        draft.update((s) => {
          s[page.key] = next;
        }, label)
      }
      undo={draft.undo}
      redo={draft.redo}
      canUndo={draft.canUndo}
      canRedo={draft.canRedo}
      onReset={() =>
        draft.update((s) => {
          s[page.key] = JSON.parse(JSON.stringify(page.defaults));
        }, 'Reset to default layout')
      }
      toolbarExtra={
        <Select
          value={previewServer?.identifier ?? 'none'}
          onValueChange={(v) =>
            void navigate({ search: (prev) => ({ ...prev, preview: v }), replace: true })
          }
        >
          <SelectTrigger className="h-8 w-48" aria-label="Preview with server">
            <SelectValue placeholder="Preview with…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No preview server</SelectItem>
            {list.map((s) => (
              <SelectItem key={s.identifier} value={s.identifier}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
    />
  );

  return (
    <div className="flex min-h-[60dvh] flex-col lg:h-[calc(100dvh-9.25rem)]">
      <div className="bg-card/60 flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-3 py-2 sm:px-4">
        <div
          role="radiogroup"
          aria-label="Server page"
          className="bg-elevated inline-flex h-8 items-center rounded-md border p-0.5"
        >
          {(Object.keys(PAGES) as Which[]).map((w) => {
            const P = PAGES[w];
            return (
              <button
                key={w}
                type="button"
                role="radio"
                aria-checked={which === w}
                onClick={() =>
                  void navigate({
                    search: (prev) => ({ ...prev, page: w === 'console' ? 'console' : undefined }),
                    replace: true,
                  })
                }
                className={cn(
                  'focus-visible:ring-ring/50 flex h-full items-center gap-1.5 rounded-[5px] px-3 text-sm font-medium outline-none focus-visible:ring-[3px]',
                  which === w
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <P.icon className="size-4" aria-hidden /> {P.label}
              </button>
            );
          })}
        </div>
        <p className="text-muted-foreground flex min-w-0 flex-1 items-start gap-1.5 text-xs">
          <Info className="mt-px size-3.5 shrink-0" aria-hidden />
          <span>{page.note}</span>
        </p>
      </div>
      <PreviewScope server={previewServer} surface={page.surface}>
        {builder}
      </PreviewScope>
    </div>
  );
}

/** Puts the preview server's live data behind the canvas, or nothing when none is picked. */
function PreviewScope({
  server,
  surface,
  children,
}: {
  server: ClientServer | undefined;
  surface: ServerPage['surface'];
  children: ReactNode;
}) {
  if (!server) return <>{children}</>;
  return (
    <ServerPageProvider key={server.identifier} server={server} surface={surface} live={false}>
      {children}
    </ServerPageProvider>
  );
}
